/**
 * 기능 플래그 관리
 */
import hasProFeature from 'utils/plan/tier'

export interface FeatureFlags {
  adminDashboard: boolean
  adminSecurity: boolean
  usePresignedUpload: boolean
  dryRun: boolean
  samlAuth: boolean
  externalImport: boolean
  importV2: boolean
  autofillIcon: boolean
  totpAuthenticator: boolean
  totpGoogleImport: boolean
  totpExport: boolean
  memoTemplate: boolean
  sharedRecordTabs: boolean
  weeklyReport: boolean
  chunkedAttachmentUpload: boolean
  attachmentDownloadGuard: boolean
  recordChangeHistory: boolean
}

const DEV_TARGETS = ['korea-dev', 'japan-dev', 'us-dev']
export const isDevBuild = DEV_TARGETS.includes(process.env.REACT_APP_TARGET || '')

export const isProdBuild = (process.env.REACT_APP_TARGET || '') === 'production'

export const featureFlags: FeatureFlags = {
  // 관리자 대시보드 — 전 환경 ON. 집계 API(/v1/admin/dashboard/*)가 배포된 뒤에만 켤 수 있다
  // (백엔드가 먼저 나가야 메뉴·라우트가 빈 화면이 되지 않는다). 되돌릴 때는 isDevBuild 로.
  adminDashboard: true,
  adminSecurity: true,
  usePresignedUpload: true,
  dryRun: false,
  samlAuth: true,
  externalImport: true,
  importV2: true,
  autofillIcon: true,
  // TOTP 운영 출시 (2026-08-06) — memoTemplate/weeklyReport 전례와 동형으로 전 환경 ON.
  // 백엔드 authenticator.json 픽스처는 develop→release 배포가 함께 나가야 탭에 템플릿이 보인다.
  totpAuthenticator: true,
  totpGoogleImport: true,
  totpExport: true,
  memoTemplate: true,
  sharedRecordTabs: true,
  weeklyReport: true,
  // 대용량 첨부 청크 업로드(SBC1 + S3 멀티파트). **쓰기 전용 게이트**다 —
  // 읽기(peekIsChunked 판별 후 스트리밍 복호)는 이 flag 와 무관하게 항상 켜져 있어야 한다.
  // 신포맷 파일이 한 번이라도 올라간 뒤 읽기를 끄면 그 파일을 아무도 열 수 없게 되므로,
  // 읽기는 킬스위치 대상이 될 수 없다. 롤백은 "쓰기만 되돌리기"로 성립한다.
  //
  // 이 flag 는 "코드 경로가 켜져 있다"는 뜻이고, **실제 활성 조건은 아니다** —
  // shouldUseChunkedUpload 가 추가로 "실효 파일당 상한 > 기존 10MB" 를 요구한다.
  // 플랜(tier)이 없으면 실효 상한이 10MB 그대로여서 신 경로는 동작하지 않고 기존 경로가 유지된다.
  // 근거: 저장 경로의 PresignedFileModel.size 가 le=MAX_FILE_SIZE(10MB) + SAFEBOX_588 로
  // 하드 게이트돼 있어(backend/api/model/resource.py:776,798-801), 상한을 FE 에서만 올리면
  // 업로드는 성공하고 저장이 588 로 실패해 S3 고아 객체만 남는다.
  chunkedAttachmentUpload: true,
  // 첨부 다운로드 보호(P4-e): 수신을 XHR 로 바꿔 진행률·취소·무진행(정지) 감지·이탈 경고를 얻는다.
  //
  // **다른 첨부 flag 와 달리 dev 전용(isDevBuild)으로 시작한다.** 이유는 활성 범위다 —
  // 업로드 청크 경로는 "실효 상한 > 10MB" 조건이 하나 더 있어 dev 에서만 도는데, 다운로드
  // 경로에는 그런 조건이 없어(helper.js 의 presigned 분기에 flag 가 없었다) 켜는 순간
  // **파일 크기와 무관하게 전 환경의 모든 첨부 다운로드**가 새 코드를 탄다.
  //
  // 특히 무진행 60초 중단은 이 시리즈에서 **"되던 것이 안 될 수 있는" 유일한 변경**이고,
  // 업로드의 같은 창(uploadManager.js:38)은 dev 전용이라 프로덕션에서 한 번도 가동된 적이 없다.
  // dev 실측 후 staging → prod 로 단계적으로 올린다.
  //
  // 범위 주의: 이 flag 는 **전송 메커니즘과 가드**를 게이팅한다. 실패 시 사용자 안내(토스트)는
  // flag 와 무관하게 유지한다 — 그것은 이미 발생한 거부에 반응할 뿐이라 자체 실패 모드가 없고,
  // 원래 고치려던 "조용한 실패"가 flag 를 내리는 순간 되살아나면 안 된다.
  attachmentDownloadGuard: isDevBuild,
  // 레코드 변경 히스토리 — dev 전용으로 시작. 조회 API(GET /v1/resources/{id}/activities)가
  // 운영에 배포·검증되기 전까지 상세 패널의 히스토리 아이콘·뷰를 숨긴다 (adminDashboard 전례).
  // 히스토리 재료 전송(편집 changed_fields, 열람·복사 activity 의 field 타입)도 이 flag 로
  // 함께 게이팅한다 — OFF 빌드의 요청 body 는 기능 도입 전과 동일. 트레이드오프: flag 를 켜기
  // 전 기간의 편집에는 필드 상세가 남지 않는다 (2026-08-18 사용자 결정, 단일 킬스위치 우선).
  // 2026-08-24 사용자 지시로 dev 포함 전 환경 OFF.
  recordChangeHistory: false,
}

// 관리자 콘솔 진입점(홈). 대시보드는 adminDashboard flag 뒤에 있어,
// flag 가 꺼진 빌드에서는 기존 구성원 목록으로 폴백한다.
export const ADMIN_CONSOLE_HOME = featureFlags.adminDashboard
  ? '/admin/dashboard'
  : '/admin/users?active=true'

// data_type 목록에서 커스텀 필드/템플릿 빌더 타입 목록에 숨겨야 하는 타입 판별 (드롭다운/템플릿 빌더 공용)
// totp 는 "생성 진입점"이라 flag 에 더해 등급(tier)도 본다 — utils/plan/tier 는 의존성 zero 라
// 여기서 import 해도 순환·무거운 체인이 없다 (utils/plan/index 를 import 하면 순환이니 금지).
export function isFeatureHiddenType(typeName: string): boolean {
  if (typeName === 'totp' && !(featureFlags.totpAuthenticator && hasProFeature())) return true
  if (typeName === 'rich_note') return true
  return false
}
